import React, { useEffect, useState } from "react";
import panelApi from "@/lib/panelApi";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  Tooltip, Legend, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Area, AreaChart,
} from "recharts";
import {
  TrendingUp, Users2, BedDouble, Calendar, AlertTriangle, Clock,
  Sparkles, DollarSign, PieChart as PieIcon,
} from "lucide-react";

// Iter 42: Statü kaldırıldı — status chart yerine Fiyat Kilit Durumu
// (Kilitli vs Bekleyen) pie chart'ı gösteriliyor. STATUS_* konstantları
// artık kullanılmıyor, kaldırıldı.
const ROOM_COLORS = ["#94A3B8", "#E85A2A", "#1B2A5C", "#D4AF37", "#10B981"];

export default function AcenteAnalytics() {
  const { user } = useAcenteAuth();
  const [data, setData] = useState(null);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    panelApi.get("/analytics").then((r) => setData(r.data)).catch(() => {});
    // Iter 42: fiyat kilit dağılımı için tüm rezervasyonlar üzerinden say
    panelApi.get("/reservations").then((r) => setReservations(r.data || [])).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-500">Yükleniyor...</div>;

  const trendData = data.monthly_trends.map((m) => ({ ...m, label: m.month.slice(2) }));
  // Iter 42: Fiyat Kilit Durumu pie chart data
  const priceLockData = (() => {
    let locked = 0, pending = 0;
    for (const r of reservations) {
      if ((r.total_price || 0) > 0) locked++;
      else pending++;
    }
    return [
      { label: "Fiyat Kilitli", count: locked, color: "#10B981" },
      { label: "Fiyat Bekliyor", count: pending, color: "#94A3B8" },
    ].filter((s) => s.count > 0);
  })();
  const roomData = (data.room_type_distribution || []).map((r, i) => ({ ...r, color: ROOM_COLORS[i] }));
  const weekdayData = data.weekday_arrivals || [];
  const revenueData = (data.monthly_revenue || []).map((m) => ({ ...m, label: m.month.slice(2) }));
  const ov = data.overview || {};

  const stats = [
    { icon: Calendar,       label: "Toplam Rezervasyon", value: ov.total_reservations, tone: "from-brand-navy to-brand-navy" },
    { icon: Sparkles,       label: "Aktif Rezervasyon",  value: ov.active_reservations, tone: "from-emerald-500 to-emerald-700" },
    { icon: Users2,         label: "Toplam Kişi",         value: ov.total_heads, tone: "from-brand-orange to-brand-orange-dark" },
    { icon: BedDouble,      label: "Toplam Oda-Gecesi",   value: ov.total_room_nights, tone: "from-amber-500 to-amber-700" },
    { icon: Clock,          label: "Ort. Grup Büyüklüğü", value: ov.avg_group_size, tone: "from-blue-500 to-blue-700" },
    { icon: Calendar,       label: "Ort. Konaklama (Gün)",value: ov.avg_nights_per_reservation, tone: "from-purple-500 to-purple-700" },
    { icon: TrendingUp,     label: "Ort. Erken Rez. (gün)",value: ov.avg_lead_days, tone: "from-rose-500 to-rose-700" },
    { icon: AlertTriangle,  label: "Eksik Bilgi",         value: ov.missing_info_reservations || 0, tone: "from-orange-400 to-orange-600" },
  ];

  return (
    <div className="space-y-6" data-testid="acente-analytics-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Analiz</div>
          <h1 className="font-serif text-3xl text-brand-navy">Panel Analitiği</h1>
          <p className="text-sm text-slate-600 mt-1">
            {user?.role === "acente"
              ? "Şirketinizin rezervasyon trendleri, öne çıkan metrikler ve dağılımlar."
              : "Tüm acenteler üzerinden rezervasyon trendleri, gelir ve operasyonel metrikler."}
          </p>
        </div>
        {data.revenue && (
          <div className="bg-brand-navy text-white rounded-2xl px-6 py-4 shadow-md" data-testid="analytics-revenue-card">
            <div className="text-xs uppercase tracking-widest text-brand-gold flex items-center gap-1"><DollarSign size={12} /> Toplam Ciro</div>
            <div className="font-serif text-3xl mt-1">{data.revenue.total.toLocaleString("tr-TR")} <span className="text-brand-gold">﷼</span></div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">SAR (Suudi Riyali)</div>
          </div>
        )}
      </header>

      {/* Upcoming 30d KPI ribbon */}
      <div className="rounded-2xl bg-gradient-to-r from-brand-orange via-brand-orange-dark to-brand-navy text-white p-5 shadow-md flex items-center gap-6 flex-wrap" data-testid="analytics-upcoming">
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
          <Sparkles size={26} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-widest text-brand-gold">Önümüzdeki 30 Gün</div>
          <div className="font-serif text-2xl mt-1">
            <span data-testid="upcoming-count">{ov.upcoming_30d_count || 0}</span> grup ·{" "}
            <span data-testid="upcoming-heads">{ov.upcoming_30d_heads || 0}</span> kişi karşılanacak
          </div>
          <div className="text-xs text-white/80 mt-1">Operasyon hazırlığı için önümüzdeki 30 gün içinde giriş yapacak toplam grup ve kişi sayısı.</div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="analytics-kpis">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className={`rounded-2xl p-4 text-white bg-gradient-to-br ${s.tone} shadow-sm relative overflow-hidden`}>
              <div className="absolute -right-4 -bottom-4 opacity-20"><Icon size={72} /></div>
              <div className="relative">
                <div className="text-[10px] uppercase tracking-widest text-white/85 flex items-center gap-1"><Icon size={12} /> {s.label}</div>
                <div className="font-serif text-3xl mt-1">{s.value ?? 0}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Monthly reservation count */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="analytics-trends-chart">
          <h3 className="font-serif text-lg text-brand-navy mb-3">Aylık Rezervasyon Sayısı (12 Ay)</h3>
          <div className="w-full h-64">
            <ResponsiveContainer>
              <AreaChart data={trendData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E85A2A" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#E85A2A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#E85A2A" strokeWidth={2.5} fill="url(#areaFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Fiyat Kilit Durumu (Iter 42 — statü yerine) */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="analytics-price-lock-chart">
          <h3 className="font-serif text-lg text-brand-navy mb-3">Fiyat Kilit Durumu</h3>
          <div className="w-full h-64">
            {priceLockData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">Rezervasyon yok</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={priceLockData} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={({ label, count }) => `${label}: ${count}`}>
                    {priceLockData.map((s) => <Cell key={s.label} fill={s.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Room type distribution */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="analytics-rooms-chart">
          <h3 className="font-serif text-lg text-brand-navy mb-3">Oda Tipi Dağılımı</h3>
          <div className="w-full h-64">
            <ResponsiveContainer>
              <BarChart data={roomData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" name="Oda Sayısı">
                  {roomData.map((r, i) => <Cell key={r.type} fill={r.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekday arrivals */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="analytics-weekday-chart">
          <h3 className="font-serif text-lg text-brand-navy mb-3">Haftanın Gününe Göre Girişler</h3>
          <div className="w-full h-64">
            <ResponsiveContainer>
              <BarChart data={weekdayData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#D4AF37" name="Giriş" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* People counts by month */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 lg:col-span-2" data-testid="analytics-people-chart">
          <h3 className="font-serif text-lg text-brand-navy mb-3">Aylık Kişi Sayıları</h3>
          <div className="w-full h-72">
            <ResponsiveContainer>
              <BarChart data={trendData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="adults" stackId="p" fill="#1B2A5C" name="Yetişkin" />
                <Bar dataKey="children" stackId="p" fill="#E85A2A" name="Çocuk" />
                <Bar dataKey="staff" stackId="p" fill="#D4AF37" name="Görevli" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly revenue (staff only) */}
        {data.monthly_revenue && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 lg:col-span-2" data-testid="analytics-revenue-chart">
            <h3 className="font-serif text-lg text-brand-navy mb-3">Aylık Ciro (12 Ay) <span className="text-xs text-slate-500 font-normal">— SAR ﷼</span></h3>
            <div className="w-full h-72">
              <ResponsiveContainer>
                <LineChart data={revenueData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v > 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString("tr-TR")} ﷼`} />
                  <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} name="Ciro (SAR)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top agencies (hidden for role=acente) */}
        {user?.role !== "acente" && data.top_agencies && data.top_agencies.length > 0 && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 lg:col-span-2" data-testid="analytics-top-agencies">
            <h3 className="font-serif text-lg text-brand-navy mb-3">En Aktif 10 Acente</h3>
            <div className="w-full h-72">
              <ResponsiveContainer>
                <BarChart data={data.top_agencies} margin={{ top: 10, right: 10, bottom: 0, left: 10 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1B2A5C" name="Rezervasyon" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Fun-fact insight strip */}
      {ov.total_reservations > 0 && (
        <div className="rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-5" data-testid="analytics-insights">
          <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold mb-3 flex items-center gap-1">
            <PieIcon size={14} /> Öne Çıkanlar
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm text-brand-navy">
            <Insight
              label="En yoğun gün"
              value={(() => {
                const best = [...weekdayData].sort((a, b) => b.count - a.count)[0];
                return best?.count ? `${best.label} — ${best.count} giriş` : "Veri yok";
              })()}
            />
            <Insight
              label="En çok tercih edilen oda"
              value={(() => {
                const best = [...roomData].sort((a, b) => b.count - a.count)[0];
                return best?.count ? `${best.label} — ${best.count} oda` : "Veri yok";
              })()}
            />
            <Insight
              label="Ortalama erken rezervasyon"
              value={`${ov.avg_lead_days || 0} gün önce yapılıyor`}
            />
            <Insight
              label="En yoğun ay"
              value={(() => {
                const best = [...(data.monthly_trends || [])].sort((a, b) => b.count - a.count)[0];
                return best?.count ? `${best.month} — ${best.count} rezervasyon` : "Veri yok";
              })()}
            />
            <Insight
              label="Toplam oda-gecesi"
              value={`${ov.total_room_nights || 0} oda × gece`}
            />
            <Insight
              label="Ortalama konaklama süresi"
              value={`${ov.avg_nights_per_reservation || 0} gün`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Insight({ label, value }) {
  return (
    <div className="rounded-xl bg-white border border-brand-navy/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
